import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'utils/i18n';
import { useDarkMode } from 'utils/darkMode';
import sharedTranslations from '../shared/translations';
import LegalToc from './LegalToc';
import LegalSearch from './LegalSearch';
import LegalSection from './LegalSection';
import { sidebar, mainContent, typography, spacing, colors } from './legalStyles';
import './LegalComponents.css';

/**
 * DocumentLayout
 * Base layout component for document-style pages with:
 * - Two-column layout (sidebar + main content)
 * - Search functionality
 * - Table of contents with scroll-spy
 * - Section rendering
 * 
 * Props:
 * - title: string - Page title
 * - lastUpdated: string - Optional last updated date
 * - sections: Array<{ id, title, content, children? }> - Sections to render
 * - renderSidebarTop: function - Optional render prop for custom sidebar header (above search/TOC)
 * - renderContentHeader: function - Optional render prop for custom main header (defaults to title)
 * - renderContentBeforeSections: function - Optional render prop for content before sections
 * - sidebarAriaLabel: string - Optional aria-label for sidebar (defaults to "Document navigation")
 */
const DocumentLayout = ({ 
  title, 
  lastUpdated, 
  sections = [],
  renderSidebarTop,
  renderContentHeader,
  renderContentBeforeSections,
  sidebarAriaLabel
}) => {
  const { language } = useTranslation();
  const { isDarkMode } = useDarkMode();
  
  // Get translations - memoized to update when language changes
  const defaultSidebarAriaLabel = useMemo(() => {
    if (sidebarAriaLabel) return sidebarAriaLabel;
    const t = sharedTranslations[language] || sharedTranslations.en;
    return t?.legal?.documentLayout?.sidebarAriaLabel || 'Document navigation';
  }, [language, sidebarAriaLabel]);
  const contentRef = useRef(null);
  const sidebarRef = useRef(null);
  const [activeId, setActiveId] = useState(sections?.[0]?.id || null);
  const [filteredSections, setFilteredSections] = useState(sections);

  // Preserve sidebar scroll position when sections change
  useEffect(() => {
    if (sidebarRef.current && sections.length > 0) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        // Don't reset scroll if user is actively scrolling
        if (sidebarRef.current) {
          // Only reset to top if we're at the very top (within 10px)
          const currentScroll = sidebarRef.current.scrollTop;
          if (currentScroll < 10) {
            sidebarRef.current.scrollTop = 0;
          }
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [sections.length]); // Only trigger on section count change, not content

  // Scroll-spy via IntersectionObserver
  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;

    const headings = container.querySelectorAll('section[data-legal-id]');
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible.length > 0) {
          const id = visible[0].target.getAttribute('data-legal-id');
          if (id) setActiveId(id);
        }
      },
      { root: null, rootMargin: '0px 0px -70% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    headings.forEach(h => observer.observe(h));
    return () => observer.disconnect();
  }, [filteredSections]);

  const onSearch = (results) => {
    // If search empty, restore original sections
    if (!results || results.length === 0) {
      setFilteredSections(sections);
      return;
    }
    setFilteredSections(results);
  };

  const containerStyle = {
    width: '100%',
    margin: '0 auto',
    padding: spacing.lg,
    boxSizing: 'border-box'
  };

  // Layout style is now handled by CSS class .legal-layout
  const layoutStyle = {};

  const sidebarStyle = {
    ...sidebar
  };

  const titleStyle = {
    ...typography.h2,
    fontSize: '16px',
    margin: `0 0 ${spacing.sm} 0`,
    color: isDarkMode ? '#e5e7eb' : colors.text
  };

  const metaStyle = {
    ...typography.small,
    margin: `0 0 ${spacing.md} 0`,
    color: isDarkMode ? '#94a3b8' : colors.muted
  };

  const mainTitleStyle = {
    ...typography.h1,
    margin: `0 0 ${spacing.sm} 0`,
    color: isDarkMode ? '#e5e7eb' : colors.text
  };

  const headerStyle = {
    marginBottom: spacing.md
  };

  // Default sidebar top content (title and meta)
  const defaultSidebarTop = (
    <div style={{ marginBottom: spacing.md }}>
      <h2 style={titleStyle}>{title}</h2>
      {lastUpdated ? (
        <div style={metaStyle}>Last updated: {lastUpdated}</div>
      ) : null}
    </div>
  );

  // Default main header
  const defaultContentHeader = (
    <header style={headerStyle}>
      <h1 style={mainTitleStyle}>{title}</h1>
      {lastUpdated ? (
        <p style={metaStyle}>Last updated: {lastUpdated}</p>
      ) : null}
    </header>
  );

  return (
    <div className="legal-layout-container" style={containerStyle}>
      {/* Main Layout Grid */}
      <div className="legal-layout" style={layoutStyle}>
        <aside ref={sidebarRef} style={sidebarStyle} aria-label={defaultSidebarAriaLabel}>
          {/* Custom sidebar top content or default */}
          {renderSidebarTop ? renderSidebarTop() : defaultSidebarTop}
          
          {/* Search */}
          <LegalSearch sections={sections} onSearch={onSearch} />
          
          {/* Table of Contents */}
          <LegalToc 
            sections={filteredSections} 
            activeId={activeId}
          />
        </aside>
        <main ref={contentRef} style={mainContent}>
          {/* Custom main header or default */}
          {renderContentHeader ? renderContentHeader() : defaultContentHeader}
          
          {/* Custom content before sections (e.g., binding notice) */}
          {renderContentBeforeSections && renderContentBeforeSections()}
          
          {/* Sections */}
          {filteredSections.map((s) => (
            <LegalSection key={s.id} section={s} level={2} />
          ))}
        </main>
      </div>
    </div>
  );
};

export default DocumentLayout;

